import { AutoComplete, Button, DatePicker, Modal, NumericInput, TimeInput } from "@pickit/pickit-components";
import { SlotCreationModalPropsType, SlotCreationValues } from "./types";
import "./SlotCreationModal.scss";
import { Field, Form, FormSpy } from "react-final-form";
import { DATE_FORMATS } from "utils/constants";
import moment from "moment";
import { validationSchema } from "./schemas";
import useValidationSchema from "hooks/useValidationSchema";
import "./i18n";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";

export const SlotCreationModal: React.FC<SlotCreationModalPropsType> = ({
  isOpen,
  regions,
  workzones,
  actualRegion,
  fetchingWorkzones,
  formRef,
  testId,
  onUpdateWorkzones,
  onSubmit,
  setDirty,
  onClose,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (isOpen) {
      params.set('modal', 'create-slot');
    } else {
      params.delete('modal');
    }
    const paramsString = params.toString();
    const newUrl = paramsString ? `${window.location.pathname}?${paramsString}` : window.location.pathname;
    window.history.pushState({}, '', newUrl);
  }, [isOpen]);

  return (
    <Modal width="1190px" height="540px" isOpen={isOpen} onClose={onClose} testId={testId}>
      <div className="creation-modal-container">
        <h2 className="creation-modal-title" data-testId={`${testId}-title`}>
          {t("slotCreation:slotCreation")}
        </h2>
        <hr className="creation-modal-separate" />
        <div className="container-slot-creation-form">
          <Form
            onSubmit={(values: SlotCreationValues) => {}}
            validate={useValidationSchema(validationSchema)}
            mutators={{
              setValue: ([field, value], state, { changeValue }) => {
                changeValue(state, field, () => value);
              },
              forceValidation: ([field], state, { changeValue }) => {
                // change the value to the same value, thus triggering a revalidation
                changeValue(state, "capacity", (value) => value);
              },
              setAutoCompleteValue: ([field], state, { changeValue }) => {
                if(!field.id) {
                  changeValue(state, "workzone", () => undefined);
                  return;
                }
                changeValue(state, field.id, () => field.name);
                if (state.lastFormState?.active === "region" && field.id !== actualRegion) {
                  changeValue(state, "workzone", () => undefined);
                  onUpdateWorkzones(field.id);
                }
              },
            }}
          >
            {({ handleSubmit, form, invalid, values }) => {
              formRef.current = form;
              return (
                <form className="form-slot-creation" onSubmit={handleSubmit}>
                  <FormSpy
                    subscription={{
                      dirty: true,
                      values: true,
                      invalid: true,
                    }}
                    onChange={(pro) => {
                      setDirty(pro.dirty);
                    }}
                  />
                  <div className="container-slot-creation-form-row">
                    <div className="container-slot-creation-form-col-1">
                      <Field
                        type="text"
                        name="region"
                        onChange={form.mutators.setAutoCompleteValue}
                        label={t("slotCreation:regionLabel")}
                        placeholder={t("slotCreation:regionPlaceholder")}
                        options={regions}
                        testId={`${testId}-region`}
                        overAll
                        keyProperty="id"
                        showProperty="name"
                        isValueObject
                      >
                        {(props: any) => <AutoComplete {...props} />}
                      </Field>
                    </div>
                    <div className="container-slot-creation-form-col-1">
                      <Field
                        type="text"
                        name="workzone"
                        onChange={form.mutators.setAutoCompleteValue}
                        label={t("slotCreation:workzoneLabel")}
                        placeholder={t("slotCreation:workzonePlaceholder")}
                        options={workzones}
                        disabled={workzones?.length === 0 || !values.region}
                        testId={`${testId}-workzone`}
                        overAll
                        keyProperty="id"
                        showProperty="name"
                        isValueObject
                        isLoading={fetchingWorkzones}
                      >
                        {(props: any) => (
                          <AutoComplete {...props} />
                        )}
                      </Field>
                    </div>
                    <div className="container-slot-creation-form-col-1">
                      <Field
                        type="text"
                        name="date"
                        language="es"
                        label={t("slotCreation:dateLabel")}
                        placeholder={t("slotCreation:datePlaceholder")}
                      >
                        {(props: any) => (
                          <DatePicker
                            singleSelection={true}
                            outputFormat={DATE_FORMATS.shortDate}
                            minDate={moment().subtract(1, "days")}
                            maxDate={moment().add(14, "days")}
                            overlayPosition="bottom"
                            overAll
                            testId={`${testId}-date`}
                            {...props}
                          />
                        )}
                      </Field>
                    </div>
                  </div>
                  <div className="container-slot-creation-form-row">
                    <div className="container-slot-creation-form-col-1">
                      <Field
                        name="slotTime.initial"
                        label={t("slotCreation:slotStartLabel")}
                        component={TimeInput}
                        placeholder={t("slotCreation:slotStartPlaceholder")}
                        testId={`${testId}-start-time`}
                        showClearIcon
                      />
                    </div>
                    <div className="container-slot-creation-form-col-1">
                      <Field
                        name="slotTime.end"
                        label={t("slotCreation:slotEndLabel")}
                        component={TimeInput}
                        placeholder={t("slotCreation:slotEndPlaceholder")}
                        testId={`${testId}-end-time`}
                        showClearIcon
                      />
                    </div>
                    <div className="container-slot-creation-form-col-1">
                      <Field
                        name="capacity"
                        label={t("slotCreation:capacityLabel")}
                        component={NumericInput}
                        maxInt={4}
                        allowDecimal={false}
                        placeholder={t("slotCreation:capacityPlaceholder")}
                        testId={`${testId}-capacity`}
                      />
                    </div>
                  </div>

                  <div className="creation-button-container">
                    <Button
                      type="button"
                      hierarchy="secondary"
                      onClick={() => onClose()}
                      testId={`${testId}-cancel-button`}
                      width="220"
                      label={t("global:cancel")}
                    />

                    <Button
                      type="button"
                      disabled={invalid}
                      hierarchy="primary"
                      width="220"
                      onClick={() =>
                        onSubmit({
                          ...values,
                          refresh: () => form.mutators.forceValidation(),
                        })
                      }
                      testId={`${testId}-create-button`}
                      label={t("global:create")}
                    />
                  </div>
                </form>
              );
            }}
          </Form>
        </div>
      </div>
    </Modal>
  );
};
